/* eslint-disable @typescript-eslint/no-empty-function */
import useFilters, {
  ChangeFilterProps,
  FilterDefinitions,
  FilterDefinitionsKeys,
} from 'hooks/useFilters/useFilters';
import { usePreventLeaveModal } from 'hooks/usePreventEditLeave/usePreventEditLeave';
import { useMemo, useState, useCallback, useContext, createContext, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '@shape/feature-flag';
import { ActiveTab, EventFilters, EventItem } from 'queries/events/types';
import { useEventsData } from './hooks/useEventsData';
import { groupEvents } from './utils/EventGrouping';
import useEventModal from './hooks/UseEventModal';
import { useTabs } from 'hooks/useTabs';
import { useTranslation } from 'react-i18next';
import { useDatesHelper } from '@shape/lighthouse-components';
import { StatusKeys } from 'components/StatusCountPanel/StatusCountPanel';
import { DateBreadcrumb } from './components/AcknowlegementBarChart/useBarDateFilter';
import { EVENTS_PAGE_LIST } from 'config/paths';
const URL_PARAM_NAME = 'eventId';

export enum ClosedByValues {
  AUTO_ACK = 'Auto Ack',
  NONE = '',
}

export type EventsContextType = {
  queryFilters: EventFilters;
  onChangeQueryFilters: (filters: EventFilters) => void;
  groupedData: EventItem[];
  dataToCount: (key: StatusKeys) => EventItem[];
  filteredData: EventItem[];
  isLoading: boolean;
  onChangeFilters: ({ filterName, filterValue }: ChangeFilterProps<EventItem>) => void;
  applyFilters: (filtersToApply: FilterDefinitions<EventItem>, data: EventItem[]) => EventItem[];
  allActiveFilters: Partial<{ [key in FilterDefinitionsKeys<EventItem>]: string[] }>;
  selectedEvent?: EventItem;
  changeSelectedEvent: (row?: EventItem) => void;
  setShowAnalytics: (value: boolean) => void;
  showAnalytics: boolean;
  enableEventsAnalytics: boolean;
  activeTab: ActiveTab;
  tabsComponent: JSX.Element | null;
  setActiveTab: (value: number) => void;
  dateBreadcrumb: DateBreadcrumb;
  setDateBreadcrumb: (breadcrumb: DateBreadcrumb) => void;
};
export const EventsContext = createContext<EventsContextType>({
  queryFilters: {},
  onChangeQueryFilters: () => {},
  groupedData: [],
  dataToCount: () => [],
  filteredData: [],
  isLoading: false,
  onChangeFilters: () => {},
  applyFilters: () => [],
  allActiveFilters: {},
  selectedEvent: undefined,
  changeSelectedEvent: () => {},
  setShowAnalytics: (_value: boolean) => {},
  showAnalytics: false,
  enableEventsAnalytics: false,
  activeTab: 'ALL',
  tabsComponent: null,
  setActiveTab: () => {},
  dateBreadcrumb: [],
  setDateBreadcrumb: () => {},
});

export const alarmsExpandFeatureFlag = 'alarms-expand';

export const ALARMS_TABS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  ALL: 'ALL',
} as const;

const EventsData = ({ children }: { children: React.ReactNode }) => {
  const { openEventModal } = useEventModal();
  const { openLeaveModal } = usePreventLeaveModal();
  const { buildDateRange } = useDatesHelper();
  const [dateBreadcrumb, setDateBreadcrumb] = useState<DateBreadcrumb>([]);
  const defaultPeriod = buildDateRange('7');
  const [queryFilters, setQueryFilters] = useState<EventFilters>({
    start_date: defaultPeriod.startDate,
    end_date: defaultPeriod.endDate,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get(URL_PARAM_NAME);
  const enableEventsAnalytics = useFeatureFlag(alarmsExpandFeatureFlag);

  const isFirstTimeSeeingAnalytics =
    enableEventsAnalytics && localStorage.getItem(alarmsExpandFeatureFlag) === null;

  const params = useParams<{ status?: ActiveTab }>();

  const statusParam = isFirstTimeSeeingAnalytics
    ? ALARMS_TABS.ALL
    : params.status ?? ALARMS_TABS.OPEN;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTab, tabsComponent, setActiveTab } = useTabs({
    tabNames: [ALARMS_TABS.OPEN, ALARMS_TABS.CLOSED, ALARMS_TABS.ALL],
    getLabel: (tab) => t(`event_page:main_tabs.${tab}`),
    defaultTab: statusParam,
    onChange: (tab) => {
      navigate(`${EVENTS_PAGE_LIST}/${tab}`, { replace: true });
      if (tab !== ALARMS_TABS.CLOSED && queryFilters.closed_by === ClosedByValues.AUTO_ACK) {
        setQueryFilters({ ...queryFilters, closed_by: ClosedByValues.NONE });
      }
    },
  });

  const { data, isLoading } = useEventsData({
    activeTab: activeTab !== ALARMS_TABS.ALL ? activeTab : undefined,
    filters: queryFilters,
  });

  const localShowAnalytics = isFirstTimeSeeingAnalytics
    ? true
    : JSON.parse(localStorage.getItem(alarmsExpandFeatureFlag) ?? 'false');
  const [showAnalytics, setShowAnalytics] = useState<boolean>(localShowAnalytics);

  const onChangeEventsExpand = useCallback((value: boolean) => {
    setShowAnalytics(value);
    localStorage.setItem(alarmsExpandFeatureFlag, String(value));
  }, []);

  const [filteredData, setFilteredData] = useState<EventItem[]>(data ?? []);
  const [selectedEvent, setSelectedEvent] = useState<EventItem>();

  const changeSelectedEvent = useCallback(
    (eventItem?: EventItem) => {
      const url = new URL(window.location.href);
      if (url.searchParams.has('edit')) {
        return openLeaveModal(() => setSelectedEvent(eventItem));
      }
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (eventItem?.id) {
          newParams.set(URL_PARAM_NAME, eventItem.id);
        } else {
          newParams.delete(URL_PARAM_NAME);
        }
        return newParams;
      });
      setSelectedEvent(eventItem);
      if (eventItem && showAnalytics) {
        return openEventModal(eventItem);
      }
    },
    [showAnalytics, openEventModal, openLeaveModal, setSearchParams],
  );
  useEffect(() => {
    if (data && data.length > 0 && selectedEventId && !selectedEvent) {
      const event = data.find((event) => selectedEventId.includes(event.id));

      changeSelectedEvent(event);
    }
  }, [selectedEventId, data, changeSelectedEvent, selectedEvent]);

  const groupedData = useMemo(() => {
    return groupEvents(data);
  }, [data]);

  const {
    onChange: onChangeFilters,
    allActiveFilters,
    applyFilters,
  } = useFilters<EventItem>({
    data: groupedData,
    onChangeFilters: (newFilteredData) => {
      if (!isLoading) {
        setFilteredData(groupEvents(newFilteredData));

        setSelectedEvent(undefined);
      }
    },
    multipleFieldSearch: ['id', 'description', 'asset.name'],
  });

  const dataToCount = useCallback(
    (key: StatusKeys) => {
      const filtersToThisKey = allActiveFilters[key as keyof typeof allActiveFilters] ?? [];

      if (filtersToThisKey.length > 0) {
        const filteredWithoutThisKey =
          applyFilters({ ...allActiveFilters, [key]: [] }, groupedData) ?? [];
        return filteredWithoutThisKey;
      }

      return filteredData;
    },
    [allActiveFilters, applyFilters, groupedData, filteredData],
  );

  const context = useMemo(
    () => ({
      allActiveFilters,
      applyFilters,
      onChangeFilters,
      queryFilters,
      onChangeQueryFilters: setQueryFilters,
      groupedData,
      dataToCount,
      filteredData,
      showAnalytics: enableEventsAnalytics && showAnalytics,
      activeTab,
      tabsComponent,
      isLoading,
      setShowAnalytics: onChangeEventsExpand,
      changeSelectedEvent,
      selectedEvent,
      enableEventsAnalytics,
      setActiveTab,
      dateBreadcrumb,
      setDateBreadcrumb,
    }),
    [
      allActiveFilters,
      applyFilters,
      onChangeFilters,
      queryFilters,
      groupedData,
      dataToCount,
      filteredData,
      showAnalytics,
      isLoading,
      activeTab,
      tabsComponent,
      onChangeEventsExpand,
      changeSelectedEvent,
      selectedEvent,
      enableEventsAnalytics,
      setActiveTab,
      dateBreadcrumb,
      setDateBreadcrumb,
    ],
  );

  return (
    <EventsContext.Provider value={context} data-testid="events-page-content-list">
      {children}
    </EventsContext.Provider>
  );
};
export default EventsData;
export const useEventContext = () => {
  return useContext(EventsContext);
};
